import { z } from "zod";

import { dbDateSchema } from "./openings";
import { publicMemberTagsSchema } from "./memberTags";

export const publicUserScheduleSchema = z.object({
  id: z.string(),
  title: z.string(),
  hours: z
    .array(
      z.object({
        day: z.number().int().min(0).max(6),
        open: z.string().optional(),
        close: z.string().optional(),
        isOpen: z.boolean(),
      }),
    )
    .optional()
    .default([]),
  index: z.number().optional().default(0),
  address: z.string().nullable().optional().default(""),
  parking: z
    .object({
      link: z.string().optional(),
      description: z.string().optional(),
    })
    .nullable()
    .optional(),
  /**
   * The services this location offers: its business schedule's assignment
   * (host ids) plus the ids of this page's own services that match them.
   * Empty means every service (#829).
   */
  assignedServiceIds: z.array(z.string()).optional().default([]),
  /**
   * The wall-clock zone the API books this location in
   * (`resolveScheduleTimezone`), so the booking window's last day matches.
   */
  timezone: z.string().optional(),
});

export const publicUserServiceOptionSchema = z.object({
  id: z.string().optional(),
  title: z.string(),
  price: z.number().optional().default(0),
  duration: z.number().optional().default(0),
  description: z.string().optional().default(""),
  referenceId: z.string().optional(),
});

export const publicUserServiceSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().optional().default(""),
  price: z.number().optional().default(0),
  duration: z.number().optional().default(0),
  up: z.boolean().optional().default(false),
  hasPayAdvance: z.boolean().optional().default(false),
  hasConsultation: z.boolean().optional().default(false),
  paymentRequirement: z
    .enum(["NONE", "DEPOSIT", "FULL"])
    .optional()
    .default("NONE"),
  depositAmount: z.number().nullable().optional().default(null),
  /**
   * Customers may book this service only at least this many minutes before
   * it starts; null means no notice. A member clone carries its business
   * service's value. The API leaves the starts that are too soon out of
   * openings and refuses them on commit; clients show
   * `booking.service.minNoticeNote`.
   */
  minBookingNoticeMinutes: z.number().int().positive().nullable().optional(),
  options: z.array(publicUserServiceOptionSchema).optional().default([]),
});

/**
 * A date range the member is unavailable. Both types block booking; only
 * `VACATION` is announced to the customer as "away until …" — `DAY_OFF` reads
 * as an ordinary non-working day. Defaults to `VACATION` so clients built
 * before the field existed keep their previous behavior.
 */
export const publicUserVacationSchema = z.object({
  startDate: dbDateSchema,
  endDate: dbDateSchema,
  type: z.enum(["DAY_OFF", "VACATION"]).optional().default("VACATION"),
});

export const publicUserPhotoSchema = z.object({
  workPhotoId: z.string().optional(),
  viewCount: z.number().int().nonnegative().optional(),
  openCount: z.number().int().nonnegative().optional(),
  id: z.string(),
  portfolioOrder: z.number().int().nonnegative().nullable().optional(),
  src: z.string(),
  alt: z
    .string()
    .optional()
    .nullable()
    .transform((value) => value ?? ""),
  type: z.enum(["image", "video"]).default("image"),
  variants: z.unknown().optional(),
});

export const publicUserVideoSchema = z.object({
  url: z.string(),
  thumbnail: z.string().optional(),
});

export const publicUserPageSchema = z.object({
  id: z.string(),
  /** Set when this page represents a team member rather than a User. */
  teamMemberId: z.string().nullable().optional().default(null),
  name: z.string(),
  username: z.string(),
  phone: z.string().nullable().optional().default(null),
  instagramHandle: z.string().nullable().optional().default(null),
  photo: z.string().optional().default(""),
  video: publicUserVideoSchema.nullable().optional().default(null),
  bio: z.string().optional().default(""),
  /** Team member tags; empty for a User page. */
  tags: publicMemberTagsSchema.optional(),
  schedules: z.array(publicUserScheduleSchema).default([]),
  services: z.array(publicUserServiceSchema).default([]),
  vacations: z.array(publicUserVacationSchema).default([]),
  photos: z.array(publicUserPhotoSchema).default([]),
});

export const publicUserPageParamsSchema = z.object({
  identifier: z
    .string()
    .min(1)
    .refine((v) => !v.includes("@"), {
      message: "Email addresses are not valid identifiers",
    }),
});

export const publicUserPageQuerySchema = z.object({
  salonId: z.string().optional(),
});

export type PublicUserSchedule = z.infer<typeof publicUserScheduleSchema>;
export type PublicUserServiceOption = z.infer<
  typeof publicUserServiceOptionSchema
>;
export type PublicUserService = z.infer<typeof publicUserServiceSchema>;
export type PublicUserVacation = z.infer<typeof publicUserVacationSchema>;
export type PublicUserPhoto = z.infer<typeof publicUserPhotoSchema>;
export type PublicUserPage = z.infer<typeof publicUserPageSchema>;
export type PublicUserPageParams = z.infer<typeof publicUserPageParamsSchema>;
export type PublicUserPageQuery = z.infer<typeof publicUserPageQuerySchema>;
